import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Sparkles, Activity, TrendingUp, Users, Award, AlertTriangle, RefreshCw, MapPin, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";

const ZW_PROVINCES = [
  { key: "Harare", d: "M245,105 l40,-8 l24,20 l-14,24 l-34,10 l-24,-18 z", cx: 275, cy: 118 },
  { key: "Bulawayo", d: "M120,240 l30,-6 l20,18 l-14,20 l-30,4 l-14,-18 z", cx: 145, cy: 250 },
  { key: "Manicaland", d: "M320,155 l55,-4 l16,60 l-24,44 l-52,4 l-18,-56 z", cx: 350, cy: 210 },
  { key: "Mashonaland Central", d: "M215,45 l70,-6 l20,28 l-18,30 l-70,4 l-18,-30 z", cx: 250, cy: 70 },
  { key: "Mashonaland East", d: "M285,90 l55,-4 l14,44 l-30,36 l-50,-2 l-14,-38 z", cx: 315, cy: 130 },
  { key: "Mashonaland West", d: "M115,80 l100,0 l14,44 l-30,44 l-90,4 l-18,-46 z", cx: 165, cy: 130 },
  { key: "Masvingo", d: "M235,220 l70,-6 l20,52 l-24,50 l-70,2 l-16,-52 z", cx: 275, cy: 265 },
  { key: "Matabeleland North", d: "M55,110 l70,-10 l24,42 l-14,50 l-70,10 l-24,-42 z", cx: 100, cy: 165 },
  { key: "Matabeleland South", d: "M60,215 l70,-6 l20,52 l-20,44 l-70,2 l-16,-46 z", cx: 100, cy: 265 },
  { key: "Midlands", d: "M155,150 l70,-4 l18,60 l-24,50 l-70,4 l-14,-58 z", cx: 195, cy: 200 },
];

const Stat = ({ label, value, hint, Icon }) => (
  <div className="border border-border bg-card p-4">
    <div className="flex items-start justify-between">
      <div>
        <div className="kicker">{label}</div>
        <div className="display text-3xl mt-1">{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      </div>
      <Icon className="h-4 w-4 text-primary" />
    </div>
  </div>
);

export default function CommandCentre() {
  const [b, setB] = useState(null);
  const [intel, setIntel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const [br, ov] = await Promise.all([
        api.get("/intel/executive-briefing"),
        api.get("/intel/overview"),
      ]);
      setB(br.data); setIntel(ov.data);
    } catch {}
    finally { setLoading(false); setRefreshing(false); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  if (loading) return (<div><Navbar /><div className="p-10">Loading command centre…</div></div>);
  const s = b?.snapshot || {};
  const maxProvinceCount = Math.max(1, ...(intel?.geography || []).map(g => g.listings));

  return (
    <div>
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="kicker">THOM Intelligence Command Centre™</div>
            <h1 className="display text-4xl">Real-time trust operations</h1>
          </div>
          <Button variant="outline" onClick={load} disabled={refreshing} data-testid="cc-refresh-btn"><RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? "animate-spin" : ""}`} /> Refresh</Button>
        </div>

        {/* Live KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          <Stat label="Inspections (24h)" value={s.inspections_24h} Icon={Activity} />
          <Stat label="Certificates (24h)" value={s.certificates_24h} Icon={Award} />
          <Stat label="Revenue (24h)" value={`$${Number(s.revenue_24h_usd).toLocaleString()}`} Icon={DollarSign} />
          <Stat label="Open Cases" value={s.open_cases} Icon={Activity} hint="live queue" />
          <Stat label="Avg Trust" value={s.avg_trust_score} Icon={TrendingUp} hint={`${s.verified_total} verified`} />
          <Stat label="Fraud Alerts" value={s.fraud_alerts} Icon={AlertTriangle} hint="open disputes" />
        </div>

        {/* AI Executive Advisor */}
        <div className="border border-primary/30 bg-primary/[0.04] p-6 mb-8">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <div className="kicker text-primary">AI Executive Advisor</div>
          </div>
          <p className="text-sm leading-relaxed max-w-4xl" data-testid="ai-briefing-text">{b?.briefing}</p>
          {(b?.recommendations || []).length > 0 && (
            <ul className="mt-3 space-y-1 text-sm">
              {b.recommendations.map((r, i) => (
                <li key={i} className="flex items-start gap-2"><TrendingUp className="h-3.5 w-3.5 mt-0.5 text-primary" /><span>{r}</span></li>
              ))}
            </ul>
          )}
          <div className="mt-3 text-[11px] mono text-muted-foreground">Generated {new Date(b?.generated_at).toLocaleTimeString()} · updates every 30s</div>
        </div>

        {/* Country map + Geography (current deployment: Zimbabwe) */}
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 border border-border bg-card p-4">
            <div className="kicker mb-2">Verification heat map · current country</div>
            <svg viewBox="0 0 420 340" className="w-full h-[380px]" data-testid="zw-map">
              <rect x="0" y="0" width="420" height="340" fill="hsl(220 14% 96%)" />
              {ZW_PROVINCES.map((p) => {
                const geo = (intel?.geography || []).find(g => g.city && (p.key.includes(g.city) || g.city === p.key || g.city === "Harare" && p.key === "Harare" || g.city === "Bulawayo" && p.key === "Bulawayo"));
                const count = geo?.listings || 0;
                const opacity = 0.15 + (count / maxProvinceCount) * 0.7;
                return (
                  <g key={p.key}>
                    <path d={p.d} fill={`hsla(160, 84%, 30%, ${opacity})`} stroke="hsl(160 20% 40%)" strokeWidth="1" />
                    <text x={p.cx} y={p.cy} fill="hsl(220 13% 9%)" fontSize="9" fontFamily="IBM Plex Mono" textAnchor="middle">{p.key.length > 12 ? p.key.slice(0,10)+'…' : p.key}</text>
                    {count > 0 && <text x={p.cx} y={p.cy + 11} fill="hsl(160 84% 20%)" fontSize="10" fontWeight="700" fontFamily="Cabinet Grotesk" textAnchor="middle">{count}</text>}
                  </g>
                );
              })}
            </svg>
            <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2"><MapPin className="h-3 w-3" /> Verification density (illustrative provincial shapes)</div>
          </div>
          <div className="border border-border bg-card p-4">
            <div className="kicker mb-3">Inspector productivity</div>
            {(intel?.inspectors || []).length === 0 ? (
              <div className="text-sm text-muted-foreground">No inspections yet.</div>
            ) : (
              <ul className="space-y-2">
                {intel.inspectors.map(i => (
                  <li key={i.inspector_id} className="flex items-center justify-between border-b border-border pb-2">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-secondary grid place-items-center text-xs font-semibold">{i.name.split(" ").map(w => w[0]).join("").slice(0,2)}</div>
                      <div>
                        <div className="text-sm font-medium">{i.name}</div>
                        <div className="text-[11px] mono text-muted-foreground">{i.inspector_id.slice(-8)}</div>
                      </div>
                    </div>
                    <div className="display text-xl">{i.completed}</div>
                  </li>
                ))}
              </ul>
            )}

            <div className="kicker mt-4 mb-3">Modules live</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center justify-between border-b border-border py-1"><span>AutoVerify™</span><span className="mono">{intel?.modules.auto_vehicles}</span></div>
              <div className="flex items-center justify-between border-b border-border py-1"><span>PropertyVerify™</span><span className="mono">{intel?.modules.property_assets}</span></div>
            </div>

            <div className="mt-4">
              <Link to="/admin" className="text-xs text-primary hover:underline">← Back to admin dashboard</Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
